// Type definitions
interface IStateConfig<T extends string> {
  onEnter?: (fromState: T) => void;
  onExit?: (toState: T) => void;
  onUpdate?: (deltaTime: number) => void;
  transitions?: IStateTransition<T>[];
}

interface IStateTransition<T extends string> {
  toState: T;
  conditions: IStateCondition[];
}

interface IStateCondition {
  type: "bool" | "float" | "string" | "trigger";
  parameter: string;
  value: any;
  comparison?: TFloatComparison; // Only for float conditions
}

export type TFloatComparison =
  | "equals"
  | "greater"
  | "less"
  | "greaterOrEqual"
  | "lessOrEqual";

export class StateMachine<T extends string> {
  private _currentState: T;
  private _states: Map<T, IStateConfig<T>> = new Map();
  private _parameters: Map<string, any> = new Map();
  private _triggers: Set<string> = new Set();

  public constructor(initialState: T) {
    this._currentState = initialState;
  }

  public addState(state: T, config: IStateConfig<T>): void {
    this._states.set(state, config);
  }

  public setBool(name: string, value: boolean): void {
    this._parameters.set(name, value);
    this._evaluateTransitions();
  }

  public setFloat(name: string, value: number): void {
    this._parameters.set(name, value);
    this._evaluateTransitions();
  }

  public setString(name: string, value: string): void {
    this._parameters.set(name, value);
    this._evaluateTransitions();
  }

  public setTrigger(name: string): void {
    this._triggers.add(name);
    this._evaluateTransitions();
    // Triggers are consumed after evaluation
    this._triggers.delete(name);
  }

  public getBool(name: string): boolean {
    return this._parameters.get(name) ?? false;
  }

  public getFloat(name: string): number {
    return this._parameters.get(name) ?? 0;
  }

  public getString(name: string): string {
    return this._parameters.get(name) ?? "";
  }

  public getCurrentState(): T {
    return this._currentState;
  }

  // Update current state
  public update(deltaTime: number): void {
    const handler = this._states.get(this._currentState);
    handler?.onUpdate?.(deltaTime);
  }

  // Automatically evaluate all transitions from current state
  private _evaluateTransitions(): void {
    const currentStateConfig = this._states.get(this._currentState);
    if (!currentStateConfig?.transitions) return;

    // Find the first transition whose conditions are met
    for (const transition of currentStateConfig.transitions) {
      if (this._evaluateConditions(transition.conditions)) {
        this._transitionTo(transition.toState);
        break; // Only take first valid transition
      }
    }
  }

  // Evaluate if all conditions in a list are met
  private _evaluateConditions(conditions: IStateCondition[]): boolean {
    return conditions.every((condition) => this._evaluateCondition(condition));
  }

  // Evaluate a single condition
  private _evaluateCondition(condition: IStateCondition): boolean {
    switch (condition.type) {
      case "bool":
        return this.getBool(condition.parameter) === condition.value;

      case "float":
        return this._compareFloat(
          this.getFloat(condition.parameter),
          condition.value,
          condition.comparison || "equals",
        );

      case "string":
        return this.getString(condition.parameter) === condition.value;

      case "trigger":
        return this._triggers.has(condition.parameter);

      default:
        return false;
    }
  }

  private _compareFloat(
    actual: number,
    expected: number,
    comparison: TFloatComparison,
  ): boolean {
    switch (comparison) {
      case "equals":
        return Math.abs(actual - expected) < 0.001;
      case "greater":
        return actual > expected;
      case "less":
        return actual < expected;
      case "greaterOrEqual":
        return actual >= expected;
      case "lessOrEqual":
        return actual <= expected;
      default:
        return false;
    }
  }

  private _transitionTo(newState: T): void {
    if (newState === this._currentState) return;

    const oldState = this._currentState;
    const oldHandler = this._states.get(oldState);
    const newHandler = this._states.get(newState);

    // Exit old state
    oldHandler?.onExit?.(newState);

    // Change state
    this._currentState = newState;

    // Enter new state
    newHandler?.onEnter?.(oldState);
  }
}
